import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Handshake, Compass } from "lucide-react";

const PERSONAS = [
    {
        icon: ShieldCheck,
        title: "Looking For A Safe Space",
        copy: "Ready to build your networking skills and connect with kindred minds, without the pressure or performance of a pitch-fest.",
        testId: "persona-safe-space",
    },
    {
        icon: Handshake,
        title: "The Referral-First Entrepreneur",
        copy: "Done with cold outreach. Ready to cultivate a network that quietly generates warm introductions on your behalf.",
        testId: "persona-referral-first",
    },
    {
        icon: Compass,
        title: "The Service Pro with Integrity",
        copy: "Wants to network without selling out, maintaining professional standards while building relationships that compound.",
        testId: "persona-service-pro",
    },
];

const WhoItsFor = () => {
    return (
        <section
            id="who"
            data-testid="who-its-for-grid"
            className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-36"
        >
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14 md:mb-20">
                <div className="max-w-2xl">
                    <div className="text-xs uppercase tracking-[0.3em] text-[#f2c652] font-display mb-4">
                        I. Who this is for
                    </div>
                    <h2 className="font-display font-black tracking-tighter text-4xl md:text-6xl leading-[0.95] text-[#f6f1e1]">
                        We're here for{" "}
                        <span className="font-serif-italic font-light text-[#f2c652]">
                            builders
                        </span>
                        , not beggars.
                    </h2>
                </div>
                <p className="max-w-md text-[#f6f1e1]/60 font-body text-base leading-relaxed">
                    This is for professionals who believe in relationships over
                    cold pitches, who want long-term, trust-based growth, and
                    who show up ready to contribute first.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#d4af37]/15 border border-[#d4af37]/20">
                {PERSONAS.map((p, idx) => {
                    const Icon = p.icon;
                    return (
                        <motion.div
                            key={p.testId}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.6, delay: idx * 0.08 }}
                            className="group relative bg-[#0e1130] p-8 md:p-10 hover:bg-[#141838] transition-colors"
                            data-testid={p.testId}
                        >
                            <div className="flex items-center justify-between mb-10">
                                <div className="w-11 h-11 rounded-full border border-[#d4af37]/30 bg-[#d4af37]/5 flex items-center justify-center group-hover:border-[#f2c652] group-hover:bg-[#f2c652]/10 transition-colors">
                                    <Icon
                                        className="w-5 h-5 text-[#d4af37] group-hover:text-[#f2c652] transition-colors"
                                        strokeWidth={1.5}
                                    />
                                </div>
                                <span className="font-display text-xs text-[#f6f1e1]/30 tracking-[0.25em]">
                                    {["I", "II", "III"][idx]}
                                </span>
                            </div>
                            <h3 className="font-display font-bold text-xl md:text-2xl tracking-tight text-[#f6f1e1] mb-3">
                                {p.title}
                            </h3>
                            <p className="text-[#f6f1e1]/60 font-body text-sm leading-relaxed">
                                {p.copy}
                            </p>
                            <div className="absolute bottom-0 left-0 h-[2px] w-0 bg-gradient-to-r from-[#d4af37] via-[#f2c652] to-[#1ce5da] transition-all duration-500 group-hover:w-full" />
                        </motion.div>
                    );
                })}
            </div>
        </section>
    );
};

export default WhoItsFor;
